import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Accessibility Context
 * Manages user accessibility preferences
 */

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const [preferences, setPreferences] = useState({
    reducedMotion: false,
    highContrast: false,
    largeText: false,
    showPatterns: true, // For colorblind users
  });

  useEffect(() => {
    // Detect system preferences
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrastQuery = window.matchMedia('(prefers-contrast: more)');
    
    setPreferences(prev => ({
      ...prev,
      reducedMotion: motionQuery.matches,
      highContrast: contrastQuery.matches,
    }));

    // Load saved preferences
    const saved = localStorage.getItem('a11y-preferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Failed to load accessibility preferences');
      }
    }

    // Listen for changes
    const handleMotionChange = (e) => {
      setPreferences(prev => ({ ...prev, reducedMotion: e.matches }));
    };
    const handleContrastChange = (e) => {
      setPreferences(prev => ({ ...prev, highContrast: e.matches }));
    };

    motionQuery.addEventListener('change', handleMotionChange);
    contrastQuery.addEventListener('change', handleContrastChange);

    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
    };
  }, []);

  const updatePreference = (key, value) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, [key]: value };
      localStorage.setItem('a11y-preferences', JSON.stringify(newPrefs));
      return newPrefs;
    });
  };

  const togglePreference = (key) => {
    updatePreference(key, !preferences[key]);
  };

  // Apply preferences to document
  useEffect(() => {
    const html = document.documentElement;
    
    if (preferences.reducedMotion) {
      html.classList.add('reduce-motion');
    } else {
      html.classList.remove('reduce-motion');
    }

    if (preferences.highContrast) {
      html.classList.add('high-contrast');
    } else {
      html.classList.remove('high-contrast');
    }

    if (preferences.largeText) {
      html.classList.add('large-text');
    } else {
      html.classList.remove('large-text');
    }
  }, [preferences]);

  return (
    <AccessibilityContext.Provider value={{
      preferences,
      updatePreference,
      togglePreference,
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityProvider;
